import { makeStyles } from '@mui/styles'
import React, { useRef, useState, useEffect, useCallback } from 'react'
import classNames from 'classnames'
import Calendar from 'react-calendar'

import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import CalendarIcon from '@assets/images/icons/calendar.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'

import ThemeInterface from '@/theme-interface'
import { getContrastText } from '@/modules/base/helpers/getContrastText'
import { Popover } from '@mui/material'
import moment from 'moment'
import config from '@/config'
import lang from '@/lang'
import { useAppSelector } from '@/redux/store'
import MarkerBase from '@/modules/signature/components/overlay/marker-base'

type DateFieldPositionMarkerProps = {
	active: boolean,
	i: number,
	dateField: {
		name: string,
		signerEmail?: string,
		position: {
			x: number,
			y: number,
			page: number,
		},
		color?: string,
		date?: string,
		signed?: boolean,
		signedAt?: string,
	},
}

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
}), { name: 'DateFieldPositionMarker' })

const DateFieldPositionMarker = (props: DateFieldPositionMarkerProps) => {
  const { dateField, active, i } = props
  const markerChildRef = useRef<HTMLDivElement>(null)

  const {
    updateItem,
    item: activeDateField,
    signingMode,
    placingMode,
    pager,
    popoverOpenForIndex,
    openRelatedPopoverForIndex,
  } = useSignatureContext()
  const { position, date, signed, signerEmail, color: signatureColor } = dateField
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const classes = useStyles()

  const userEmail = useAppSelector(state => state.user.info.email)
  const isTaxConsultant = useAppSelector(state => state.tenant.info.isTaxConsultant)

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (popoverOpenForIndex === i) {
      // necessary to position popover correctly
      timeoutId = setTimeout(() => {
        setDatePickerOpen(true)
        openRelatedPopoverForIndex(undefined)
      }, 50)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [popoverOpenForIndex, openRelatedPopoverForIndex, i])

  const handleDateFieldMarkerClick = () => {
    if (!signingMode && !placingMode) { return }
    if (signingMode && (userEmail !== signerEmail && !isTaxConsultant)) { return }
    setDatePickerOpen(true)
  }

  const handleDateChange = (date) => {
    updateItem({
      ...activeDateField,
      date: moment(date).toISOString(),
    })
    setDatePickerOpen(false)
  }

  const handleDatePickerClose = useCallback(() => {
    setDatePickerOpen(false)
  }, [])

  if (!pager?.currentPage || pager.currentPage !== position.page || signed) return null

  return (
    <MarkerBase
      i={i}
      active={active}
      position={position}
      signed={signed}
      signatureColor={signatureColor}
      onClick={handleDateFieldMarkerClick}
      ref={markerChildRef}
    >
      <>
        <SVGIcon
          svg={CalendarIcon}
          height="13px"
          width="13px"
          fill={getContrastText(signatureColor)} />
        <div className={classes.content}>
          <span>{date ? moment(date).format(config.dateFormat.client) : lang.signature.dateLabel}</span>
        </div>
        {datePickerOpen && (
          <Popover
            open={datePickerOpen}
            onClose={handleDatePickerClose}
            anchorEl={markerChildRef.current}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}>
            <Calendar onChange={handleDateChange} value={date} />
          </Popover>
        )}
      </>
    </MarkerBase>
  )
}

export default DateFieldPositionMarker
